import React from "react";
import { PageHeader, PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { FullWidthDropdown } from "component/shared/full-width-dropdown";
import { DashboardLayout } from "component/layout/dashboard";
import { YesNoToggleField } from "component/shared/yes-no-toggle-field";
import { User } from "model/user";
import { bindActionCreators, Dispatch } from "redux";
import { saveUser } from "redux/slice/authentication";
import { fetchPages } from "redux/slice/pages";
import { getPages } from "redux/selector";
import { RootState } from "redux/store";
import { PageProps } from "shared/page-props";
import { ActiveResultsSortType } from "type/active-results-sort";
import { EurekaResultsSortType } from "type/eureka-results-sort";
import { FeaturedSortCodeType } from "type/featured-sort-code";
import { ListingResultsDisplayType } from "type/listing-results-display";
import { SoldsOnFeaturedType } from "type/solds-on-featured";
import { FeaturedPendingResultsType } from "type/featured-pending-results";
import { LimitSoldResultsByMonthsType } from "type/limit-sold-results-by-months";
import { PageType } from "type/page-type";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { UnderlineTabs } from "component/shared/underline-tabs";
import { FeaturedMlsIdsContainer } from "component/page/featured-mls-ids/container";
import { hasMlsIdsTab } from "component/page/featured-mls-ids/access";
import MuiAlert, { AlertColor as Color } from "@mui/material/Alert";
import { styles } from "./style";

const SMALL_LOGO_URL_MAX_LENGTH = 500;

interface Props
extends PageProps, WithStyles<typeof styles>,
		Router.RouteComponentProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User,
	label: string,
}

interface State {
	activeResultsSort: ActiveResultsSortType;
	automatedFeaturedSolds: boolean;
	defaultResultsView: "map" | "list";
	displayMapGridResults: boolean;
	displayMapListResults: boolean;
	eurekaResultsSort: EurekaResultsSortType;
	featuredSortCode: FeaturedSortCodeType;
	hideSoldSearch: boolean;
	listingResultsDisplay: ListingResultsDisplayType;
	limitSoldResultsByMonths: LimitSoldResultsByMonthsType;
	saving: boolean;
	showClientListingsFirst: boolean;
	featuredPendingResults: FeaturedPendingResultsType;
	soldsOnFeatured: boolean;
	showSupplementalPropertiesOnFeatured: boolean;
	smallLogoImageSource: "upload" | "url";
	smallLogoUrl: string;
	resultsPerPage: number;
	openSnackbar: boolean;
	snackbarMessage: string;
	snackbarSeverity?: Color;
	// With the MLS Setup redesign on, the page has an MLS IDs tab before today's settings.
	tab: "mlsIds" | "settings";
}

const mapStateToProps = (state: RootState) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
		pages: getPages(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
			fetchPages,
		},
		dispatch
	);

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		const isGridMode = props.user.listingResultsDisplay?.grid ?? false;
		const mapVisible = isGridMode ? props.user.displayMapGridResults : props.user.displayMapListResults;
		this.state = {
			...props.user,
			saving: false,
			smallLogoImageSource: "url",
			resultsPerPage: props.user.resultsPerPage ? props.user.resultsPerPage : 24,
			openSnackbar: false,
			snackbarMessage: "",
			defaultResultsView: mapVisible ? "map" : "list",
			// ?tab=settings opens on the settings. The tab is kept in state after that, so switching it isn't a
			// navigation the unsaved-changes prompt would stop.
			tab: hasMlsIdsTab(props.user) && new URLSearchParams(props.location.search).get("tab") !== "settings" ? "mlsIds" : "settings",
		};
	}

	public componentDidMount() {
		this.props.fetchPages();
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError } = this.props;
		if (prevProps.authLoading && !authLoading && authError != null && this.state.saving) {
			this.setState({
				saving: false,
				openSnackbar: true,
				snackbarMessage: authError || "Failed to save settings",
				snackbarSeverity: "error",
			});
		}
	}

	private isDirty(): boolean {
		const { user } = this.props;
		const isGridMode = user.listingResultsDisplay?.grid ?? false;
		const savedMapVisible = isGridMode ? user.displayMapGridResults : user.displayMapListResults;
		const savedDefaultResultsView: "map" | "list" = savedMapVisible ? "map" : "list";
		return (
			this.state.automatedFeaturedSolds !== user.automatedFeaturedSolds ||
			this.state.soldsOnFeatured !== user.soldsOnFeatured ||
			this.state.featuredPendingResults?.id !== user.featuredPendingResults?.id ||
			this.state.showSupplementalPropertiesOnFeatured !== user.showSupplementalPropertiesOnFeatured ||
			this.state.smallLogoUrl !== user.smallLogoUrl ||
			this.state.hideSoldSearch !== user.hideSoldSearch ||
			this.state.activeResultsSort?.id !== user.activeResultsSort?.id ||
			this.state.eurekaResultsSort?.id !== user.eurekaResultsSort?.id ||
			this.state.featuredSortCode?.id !== user.featuredSortCode?.id ||
			this.state.limitSoldResultsByMonths?.id !== user.limitSoldResultsByMonths?.id ||
			this.state.listingResultsDisplay?.id !== user.listingResultsDisplay?.id ||
			this.state.showClientListingsFirst !== user.showClientListingsFirst ||
			this.state.resultsPerPage !== (user.resultsPerPage || 24) ||
			this.state.defaultResultsView !== savedDefaultResultsView
		);
	}

	private renderNavModal() {
		return <UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />;
	}

	public save() {
		const { saveUser, user: propUser } = this.props;
		const {
			activeResultsSort,
			eurekaResultsSort,
			featuredSortCode,
			listingResultsDisplay,
			limitSoldResultsByMonths,
			featuredPendingResults,
			defaultResultsView,
		} = this.state;
		const isV10 = propUser.kestrelVersion.all;
		const isGridMode = propUser.listingResultsDisplay?.grid ?? false;
		const displayMapGridResults = isGridMode ? (defaultResultsView === "map") : propUser.displayMapGridResults;
		const displayMapListResults = !isGridMode ? (defaultResultsView === "map") : propUser.displayMapListResults;
		const user = {
			...this.state,
			// Sort fields are managed by Search > General for V10; preserve existing values
			activeResultsSortId: isV10 ? propUser.activeResultsSort?.id : activeResultsSort.id,
			eurekaResultsSortId: isV10 ? propUser.eurekaResultsSort?.id : eurekaResultsSort.id,
			featuredSortCodeId: isV10 ? propUser.featuredSortCode?.id : featuredSortCode.id,
			listingResultsDisplayId: listingResultsDisplay.id,
			// limitSoldResultsByMonths is managed by Search > General for V10
			limitSoldResultsByMonthsId: isV10 ? propUser.limitSoldResultsByMonths?.id : limitSoldResultsByMonths.id,
			featuredPendingResultsId: featuredPendingResults.id,
			resultsPerPage: this.state.resultsPerPage,
			displayMapGridResults,
			displayMapListResults,
		};
		saveUser({user});
		this.setState({ saving: true });
	}

	public cancel() {
		const { user } = this.props;
		const isGridMode = user.listingResultsDisplay?.grid ?? false;
		const mapVisible = isGridMode ? user.displayMapGridResults : user.displayMapListResults;
		this.setState({
			...user,
			saving: false,
			smallLogoImageSource: "url",
			resultsPerPage: user.resultsPerPage ? user.resultsPerPage : 24,
			openSnackbar: false,
			snackbarMessage: "",
			defaultResultsView: mapVisible ? "map" : "list",
			tab: this.state.tab,
		});
	}

	public render() {
		const { authError, authLoading, user, pages, classes = {} } = this.props;
		const dirty = this.isDirty();
		const eurekaSearch = user.permissions.eurekaSearch && user.eurekaEnabled;
		const {
			activeResultsSort,
			automatedFeaturedSolds,
			defaultResultsView,
			eurekaResultsSort,
			featuredSortCode,
			listingResultsDisplay,
			saving,
			featuredPendingResults,
			showSupplementalPropertiesOnFeatured,
			smallLogoImageSource,
			smallLogoUrl,
			openSnackbar,
			snackbarMessage,
			snackbarSeverity,
			soldsOnFeatured,
			hideSoldSearch,
			limitSoldResultsByMonths,
			showClientListingsFirst,
		} = this.state;

		const featuredListingsPage = pages?.find((p) => p.type.id === PageType.FEATURED_LISTINGS.id);
		const featuredListingsPageUrl = featuredListingsPage ? user.baseUrl + featuredListingsPage.path : undefined;

		const title = "Featured Listings";

		const mlsIdsTab = hasMlsIdsTab(user);
		const tabs = mlsIdsTab ? (
			<Mui.Box mb={3}>
				<UnderlineTabs
					aria-label="Featured Listings"
					value={this.state.tab}
					onChange={(tab) => this.setState({ tab })}
					tabs={[
						{ value: "mlsIds", label: "MLS IDs" },
						{ value: "settings", label: "Settings" },
					]}
				/>
			</Mui.Box>
		) : null;

		// With the MLS IDs tab, one description under the title covers both tabs.
		const header = mlsIdsTab ? (
			<PageHeader
				title={title}
				description="Your MLS IDs determine which listings appear on your Featured Listings page. Set them up, then control how listings display, including sold, pending, and supplemental listings."
				action={featuredListingsPageUrl && <PageHeaderLink href={featuredListingsPageUrl}>View Featured Listings Page</PageHeaderLink>}
			/>
		) : (
			<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
				<Mui.Typography variant="h1">{title}</Mui.Typography>
				{featuredListingsPageUrl && (
					<PageHeaderLink href={featuredListingsPageUrl}>View Featured Listings Page</PageHeaderLink>
				)}
			</Mui.Grid>
		);

		if (mlsIdsTab && this.state.tab === "mlsIds") {
			return (
				<DashboardLayout
					permitted={user && user.permissions.listingResults}
					title={title}
					header={header}
				>
					{tabs}
					<FeaturedMlsIdsContainer />
					{this.renderNavModal()}
				</DashboardLayout>
			);
		}

		if (user.kestrelVersion.all) {
			return (
				<DashboardLayout
					permitted={user && user.permissions.listingResults}
					title={title}
					header={header}
				>
					{tabs}
					{!mlsIdsTab && (
						<>
							<Mui.Typography variant="body2" className={classes.introText}>
								Control how featured listings display on your website (including sold, pending, and supplemental listings).
							</Mui.Typography>
							<Mui.Typography variant="body2" className={classes.introText} style={{ marginBottom: 32 }}>
								Looking for Search Result Settings?{" "}
								Go to <Router.Link to={paths.settingsSearchGeneral} className={classes.marketLink}>Search &gt; General</Router.Link>
							</Mui.Typography>
						</>
					)}
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography className={classes.sectionHeader}>MLS Data Sync</Mui.Typography>
								<Mui.Tooltip title="Control whether your featured listing statuses (active, pending, sold) update automatically from your MLS feed.">
									<Mui.IconButton size="small" className={classes.infoIcon}>
										<Icons.Info size={16} />
									</Mui.IconButton>
								</Mui.Tooltip>
							</Mui.Box>
							<Mui.FormControlLabel
								style={{ alignItems: "flex-start" }}
								classes={{ label: classes.toggleLabelContent }}
								control={
									<Mui.Switch
										checked={automatedFeaturedSolds}
										onChange={(e) => this.setState({ automatedFeaturedSolds: e.target.checked })}
										color="secondary"
									/>
								}
								label={
									<>
										<Mui.Typography className={classes.toggleLabel}>Automatically sync featured listing statuses with MLS</Mui.Typography>
										<Mui.Typography variant="body2" className={classes.sectionDescription}>
											If disabled, listing statuses must be updated manually and sold listings appear only if provided by your MLS. Must be enabled for your co-listings and buyer's agent to appear on your sold listings, when provided by your MLS.
										</Mui.Typography>
									</>
								}
							/>
						</FullPageCard>
					</Mui.Box>
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography className={classes.sectionHeader}>Sold Listings</Mui.Typography>
								<Mui.Tooltip title="Choose whether your sold featured listings appear on the Featured Listings page or only on the Sold Featured Listings page. This includes any sold featured listings in the last 2 years.">
									<Mui.IconButton size="small" className={classes.infoIcon}>
										<Icons.Info size={16} />
									</Mui.IconButton>
								</Mui.Tooltip>
							</Mui.Box>
							<Mui.FormControlLabel
								style={{ alignItems: "flex-start" }}
								classes={{ label: classes.toggleLabelContent }}
								control={
									<Mui.Switch
										checked={soldsOnFeatured}
										onChange={(e) => this.setState({ soldsOnFeatured: e.target.checked })}
										color="secondary"
									/>
								}
								label={
									<>
										<Mui.Typography className={classes.toggleLabel}>Show sold listings on Featured Listings Page</Mui.Typography>
										<Mui.Typography variant="body2" className={classes.sectionDescription}>
											Sold featured listings already display on your Sold Featured Listings page, enable to also display on your Featured Listings Page.
										</Mui.Typography>
									</>
								}
							/>
						</FullPageCard>
					</Mui.Box>
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography className={classes.sectionHeader}>Pending Listings</Mui.Typography>
								<Mui.Tooltip title="Displaying pending listings on other pages can provide additional exposure since Featured Listings can display in multiple places throughout your site (e.g. listing galleries, widgets, gallery sliders, and slideshows)">
									<Mui.IconButton size="small" className={classes.infoIcon}>
										<Icons.Info size={16} />
									</Mui.IconButton>
								</Mui.Tooltip>
							</Mui.Box>
							<Mui.Typography variant="body2" className={classes.sectionDescription} style={{ marginBottom: 8 }}>
								Pending listings always display on the Pending Listings IDX page. Choose where else they should display:
							</Mui.Typography>
							<Mui.RadioGroup
								className={classes.radioGroupStacked}
								value={featuredPendingResults.id}
								onChange={(event) =>
									this.setState({
										featuredPendingResults: FeaturedPendingResultsType.getByIdOrDefault(Number(event.target.value)),
									})
								}
							>
								{FeaturedPendingResultsType.values().map((value) => (
									<Mui.FormControlLabel
										key={value.id}
										label={value.label}
										control={<Mui.Radio color="secondary" />}
										value={value.id}
									/>
								))}
							</Mui.RadioGroup>
						</FullPageCard>
					</Mui.Box>
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography className={classes.sectionHeader}>Supplemental Listings</Mui.Typography>
								<Mui.Tooltip title="Supplemental listings are manually added listings not in your MLS IDX feed (e.g., pocket listings or out-of-board listings). You may only add listings you or your brokerage represent — adding others' listings may violate your real estate board's advertising rules.">
									<Mui.IconButton size="small" className={classes.infoIcon}>
										<Icons.Info size={16} />
									</Mui.IconButton>
								</Mui.Tooltip>
							</Mui.Box>
							<Mui.FormControlLabel
								style={{ alignItems: "flex-start" }}
								classes={{ label: classes.toggleLabelContent }}
								control={
									<Mui.Switch
										checked={showSupplementalPropertiesOnFeatured}
										onChange={(e) => this.setState({ showSupplementalPropertiesOnFeatured: e.target.checked })}
										color="secondary"
									/>
								}
								label={
									<>
										<Mui.Typography className={classes.toggleLabel}>Display supplemental listings on Featured Listings Page</Mui.Typography>
										<Mui.Typography variant="body2" className={classes.sectionDescription}>
											Enable to also display supplemental listings on the Featured Listings Page. When disabled, supplemental listings will only display on the dedicated Supplemental Listings page.
										</Mui.Typography>
									</>
								}
							/>
						</FullPageCard>
					</Mui.Box>
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Typography className={classes.sectionHeader} style={{ marginBottom: 16 }}>
								Featured Listings Logo
							</Mui.Typography>
							<Mui.Typography variant="body2" className={classes.sectionDescription} style={{ marginBottom: 16 }}>
								Your logo will display with your featured listings everywhere they appear, such as search results, the Featured Listings page, and gallery sliders.
							</Mui.Typography>
							<Mui.RadioGroup
								className={classes.radioGroupStacked}
								value={smallLogoImageSource}
								onChange={(event) =>
									this.setState({
										smallLogoImageSource: event.target.value as "upload" | "url",
									})
								}
							>
								<Mui.FormControlLabel
									label="Use Image URL"
									control={<Mui.Radio color="secondary" />}
									value="url"
								/>
								<Mui.FormControlLabel
									label="Upload Image"
									control={<Mui.Radio color="secondary" />}
									value="upload"
								/>
							</Mui.RadioGroup>
							{smallLogoImageSource === "url" && (
								<Mui.TextField
									fullWidth
									inputProps={{ maxLength: SMALL_LOGO_URL_MAX_LENGTH }}
									label="Image URL"
									onChange={(event) =>
										this.setState({ smallLogoUrl: event.target.value })
									}
									placeholder="Enter Image URL (include http://)"
									value={smallLogoUrl}
									variant="outlined"
									style={{ maxWidth: 576, marginTop: 16 }}
								/>
							)}
							{smallLogoImageSource === "upload" && (
								<PhotoUploadDialogButton
									multiple={false}
									callback={(uploads) => {
										const upload = uploads.find((item) => item.url);
										if (upload?.url) {
											this.setState({
												smallLogoImageSource: "url",
												smallLogoUrl: upload.url,
												openSnackbar: true,
												snackbarMessage: "Upload Successful",
												snackbarSeverity: "success",
											});
										} else {
											this.setState({
												openSnackbar: true,
												snackbarMessage: "Error",
												snackbarSeverity: "error",
											});
										}
									}}
									label="Upload Image"
									resourceType="image"
									variant="text"
									color="primary"
									endIcon={<Icons.Upload size={16} />}
								/>
							)}
						</FullPageCard>
					</Mui.Box>
					<SettingsStickyFooter dirty={dirty} saving={saving} authError={authError} onSave={() => this.save()} onCancel={() => this.cancel()} />
					<Mui.Snackbar
						open={saving && !authLoading && !authError}
						message="Settings saved"
						autoHideDuration={6000}
						onClose={() => this.setState({ saving: false })}
						action={
							<Mui.IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() => this.setState({ saving: false })}
							>
								<Icons.X fontSize="small" />
							</Mui.IconButton>
						}
					>
						<MuiAlert severity="info">
							<Mui.Typography>Settings saved</Mui.Typography>
						</MuiAlert>
					</Mui.Snackbar>
					<Mui.Snackbar
						action={
							<Mui.IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() => this.setState({
									openSnackbar: false,
								})}
							>
								<Icons.Link fontSize="small" />
							</Mui.IconButton>
						}
						autoHideDuration={6000}
						onClose={() => this.setState({
							openSnackbar: false,
						})}
						open={openSnackbar}
					>
						<MuiAlert severity={snackbarSeverity}>
							<Mui.Typography>{snackbarMessage}</Mui.Typography>
						</MuiAlert>
					</Mui.Snackbar>
					{this.renderNavModal()}
				</DashboardLayout>
			);
		}

		return (
			<DashboardLayout
				permitted={user && user.permissions.listingResults}
				title={title}
				header={
					<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h1">{title}</Mui.Typography>
						{featuredListingsPageUrl && (
							<Mui.Link href={featuredListingsPageUrl} target="_blank" rel="noopener noreferrer">
								<Mui.Button variant="outlined" size="small" endIcon={<Icons.ExternalLink size={14} />}>
									View Featured Listings Page
								</Mui.Button>
							</Mui.Link>
						)}
					</Mui.Grid>
				}
			>
				{tabs}
				<FullPageCard>
					<Mui.Grid container direction="column" spacing={3}>
						{/* Listings Section */}
						<Mui.Grid item>
							<Mui.Typography className={classes.sectionHeader}>
								Listings
							</Mui.Typography>
						</Mui.Grid>

						<Mui.Grid item>
							<Mui.Typography className={classes.fieldLabel}>
								Featured Listings
							</Mui.Typography>
							<div className={classes.toggleContainer}>
								<YesNoToggleField
									label="Display supplemental listings on Featured Listings Page"
									value={showSupplementalPropertiesOnFeatured}
									onChange={(value) =>
										this.setState({
											showSupplementalPropertiesOnFeatured: value,
										})
									}
								/>
								<YesNoToggleField
									label="Automatically sync featured listing statuses with MLS"
									value={automatedFeaturedSolds}
									onChange={(value) =>
										this.setState({
											automatedFeaturedSolds: value,
										})
									}
									sidebarNote="Updates your featured listing statuses automatically using MLS data. Only listings currently searchable on your site will display. Must be enabled for buyer's agent listings and co-listings to display. If disabled, statuses must be updated manually, and sold listings appear only if provided by your MLS."
								/>
							</div>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography className={classes.fieldLabel}>
								Featured Listings Logo
							</Mui.Typography>
							<Mui.RadioGroup
								className={classes.radioGroupStacked}
								value={smallLogoImageSource}
								onChange={(event) =>
									this.setState({
										smallLogoImageSource: event.target.value as "upload" | "url",
									})
								}
							>
								<Mui.FormControlLabel
									label="Use Image URL"
									control={<Mui.Radio color="secondary" />}
									value="url"
								/>
								<Mui.FormControlLabel
									label="Upload Image"
									control={<Mui.Radio color="secondary" />}
									value="upload"
								/>
							</Mui.RadioGroup>
							{smallLogoImageSource === "url" && (
								<Mui.TextField
									fullWidth
									inputProps={{ maxLength: SMALL_LOGO_URL_MAX_LENGTH }}
									label="Image URL"
									onChange={(event) =>
										this.setState({ smallLogoUrl: event.target.value })
									}
									placeholder="Enter Image URL (include http://)"
									value={smallLogoUrl}
									variant="outlined"
									style={{ maxWidth: 576, marginTop: 16 }}
								/>
							)}
							{smallLogoImageSource === "upload" && (
								<PhotoUploadDialogButton
									multiple={false}
									callback={(uploads) => {
										const upload = uploads.find((item) => item.url);
										if (upload?.url) {
											this.setState({
												smallLogoImageSource: "url",
												smallLogoUrl: upload.url,
												openSnackbar: true,
												snackbarMessage: "Upload Successful",
												snackbarSeverity: "success",
											});
										} else {
											this.setState({
												openSnackbar: true,
												snackbarMessage: "Error",
												snackbarSeverity: "error",
											});
										}
									}}
									label="Upload Image"
									resourceType="image"
									variant="text"
									color="primary"
									endIcon={<Icons.Upload size={16} />}
								/>
							)}
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography className={classes.fieldLabel}>
								Pending Properties
							</Mui.Typography>
							{user.kestrelVersion.all ? (
								<Mui.RadioGroup
									className={classes.radioGroupStacked}
									value={featuredPendingResults.id}
									onChange={(event) =>
										this.setState({
											featuredPendingResults: FeaturedPendingResultsType.getByIdOrDefault(Number(event.target.value)),
										})
									}
								>
									{FeaturedPendingResultsType.values().map((value) => (
										<Mui.FormControlLabel
											key={value.id}
											label={value.label}
											control={<Mui.Radio color="secondary" />}
											value={value.id}
										/>
									))}
								</Mui.RadioGroup>
							) : (
								<FullWidthDropdown<number>
									label="Display pending properties on..."
									onChange={(value) =>
										this.setState({
											featuredPendingResults:
												FeaturedPendingResultsType.getByIdOrDefault(value),
										})
									}
									value={featuredPendingResults.id}
									maxWidth={576}
								>
									{FeaturedPendingResultsType.values().map((value) => (
										<Mui.MenuItem key={value.id} value={value.id}>
											{value.label}
										</Mui.MenuItem>
									))}
								</FullWidthDropdown>
							)}
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography className={classes.fieldLabel}>
								Sold Properties
							</Mui.Typography>
							{user.kestrelVersion.all ? (
								<div className={classes.toggleContainer}>
									<YesNoToggleField
										label="Show sold listings on Featured Listings Page"
										value={soldsOnFeatured}
										onChange={(value) =>
											this.setState({ soldsOnFeatured: value })
										}
									/>
								</div>
							) : (
								<>
									<div className={classes.toggleContainer}>
										<YesNoToggleField
											label="Show sold listings in search results"
											value={!hideSoldSearch}
											onChange={(value) =>
												this.setState({ hideSoldSearch: !value })
											}
										/>
									</div>
									<FullWidthDropdown<number>
										label="Show sold properties on..."
										onChange={(value) =>
											this.setState({ soldsOnFeatured: !!value })
										}
										value={soldsOnFeatured ? 1 : 0}
										maxWidth={576}
									>
										{SoldsOnFeaturedType.values().map((value) => (
											<Mui.MenuItem key={value.label} value={value.id ? 1 : 0}>
												{value.label}
											</Mui.MenuItem>
										))}
									</FullWidthDropdown>
									<Mui.Box mt={2}>
										<FullWidthDropdown<number>
											label="Show sold in last..."
											onChange={(value) =>
												this.setState({ limitSoldResultsByMonths: LimitSoldResultsByMonthsType.getByIdOrDefault(value) })
											}
											value={limitSoldResultsByMonths.id}
											maxWidth={576}
										>
											{LimitSoldResultsByMonthsType.values().map((value) => (
												<Mui.MenuItem key={value.id} value={value.id}>
													{value.label}
												</Mui.MenuItem>
											))}
										</FullWidthDropdown>
									</Mui.Box>
								</>
							)}
						</Mui.Grid>
						{!user.kestrelVersion.all && (
							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>
									Prioritize Own Listings
								</Mui.Typography>
								<div className={classes.toggleContainer}>
									<YesNoToggleField
										label="Show own listings first in search results"
										value={showClientListingsFirst}
										onChange={(value) =>
											this.setState({ showClientListingsFirst: value })
										}
									/>
								</div>
							</Mui.Grid>
						)}
						{!user.kestrelVersion.all && (
							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>
									Default Sort
								</Mui.Typography>
								{eurekaSearch ? (
									<FullWidthDropdown<string>
										label="Sort listings by..."
										onChange={(value) => {
											this.setState({
												eurekaResultsSort: EurekaResultsSortType.getByIdOrDefault(value),
											})
										}}
										value={eurekaResultsSort.id}
										maxWidth={576}
									>
										{EurekaResultsSortType.values().map((value) => (
											<Mui.MenuItem key={value.id} value={value.id}>
												{value.label}
											</Mui.MenuItem>
										))}
									</FullWidthDropdown>
								) : (
									<FullWidthDropdown<string>
										label="Sort listings by..."
										onChange={(value) => {
											this.setState({
												activeResultsSort: ActiveResultsSortType.getByIdOrDefault(value),
											})
										}}
										value={activeResultsSort.id}
										maxWidth={576}
									>
										{ActiveResultsSortType.values().map((value) => (
											<Mui.MenuItem key={value.id} value={value.id}>
												{value.label}
											</Mui.MenuItem>
										))}
									</FullWidthDropdown>
								)}
							</Mui.Grid>
						)}
						{!user.kestrelVersion.all && (
							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>
									Default Search Results Layout
								</Mui.Typography>
								<Mui.RadioGroup
									value={listingResultsDisplay.id}
									onChange={(e) => this.setState({ listingResultsDisplay: ListingResultsDisplayType.getByIdOrDefault(e.target.value) })}
								>
									{ListingResultsDisplayType.values().map((option) => (
										<Mui.FormControlLabel
											key={option.id}
											value={option.id}
											control={<Mui.Radio color="secondary" />}
											label={option.grid ? "Grid View" : "List View"}
										/>
									))}
								</Mui.RadioGroup>
							</Mui.Grid>
						)}
						{!user.kestrelVersion.all && (
							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>
									Default Search Results View (Desktop)
								</Mui.Typography>
								<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
									Sets the default view for listing search results on desktop. Mobile visitors will be shown the list view by default.
								</Mui.Typography>
								<Mui.RadioGroup
									value={defaultResultsView}
									onChange={(e) => this.setState({ defaultResultsView: e.target.value as "map" | "list" })}
								>
									<Mui.FormControlLabel value="map" control={<Mui.Radio color="secondary" />} label="Show List & Map" />
									<Mui.FormControlLabel value="list" control={<Mui.Radio color="secondary" />} label="Show List Only" />
								</Mui.RadioGroup>
							</Mui.Grid>
						)}
						{!user.kestrelVersion.all && (
							<Mui.Grid item>
								<FullWidthDropdown<string>
									label="Featured Listings Sort"
									onChange={(value) =>
										this.setState({
											featuredSortCode: FeaturedSortCodeType.getByIdOrDefault(value),
										})
									}
									value={featuredSortCode.id}
									maxWidth={576}
								>
									{FeaturedSortCodeType.values().map((value) => (
										<Mui.MenuItem key={value.id} value={value.id}>
											{value.label}
										</Mui.MenuItem>
									))}
								</FullWidthDropdown>
							</Mui.Grid>
						)}
						{(!user.permissions.eurekaSearch || !user.eurekaEnabled) &&
							<>
								{!user.kestrelVersion.all &&
									<Mui.Grid item xs={12}>
										<FullWidthDropdown<number>
											label="Results Per Page"
											onChange={(value) => {
												this.setState({resultsPerPage: value})
											}}
											value={this.state.resultsPerPage}
											maxWidth={576}
										>
											<Mui.MenuItem value={12}>12</Mui.MenuItem>
											<Mui.MenuItem value={24}>24</Mui.MenuItem>
											<Mui.MenuItem value={36}>36</Mui.MenuItem>
											<Mui.MenuItem value={48}>48</Mui.MenuItem>
										</FullWidthDropdown>
									</Mui.Grid>
								}
							</>
						}
					</Mui.Grid>
					<Mui.Snackbar
						open={saving && !authLoading && !authError}
						message="Settings saved"
						autoHideDuration={6000}
						onClose={() => this.setState({ saving: false })}
						action={
							<Mui.IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() => this.setState({ saving: false })}
							>
								<Icons.X fontSize="small" />
							</Mui.IconButton>
						}
					>
						<MuiAlert severity="info">
							<Mui.Typography>Settings saved</Mui.Typography>
						</MuiAlert>
					</Mui.Snackbar>
				</FullPageCard>
				<SettingsStickyFooter dirty={dirty} saving={saving} authError={authError} onSave={() => this.save()} onCancel={() => this.cancel()} />
				<Mui.Snackbar
					action={
						<Mui.IconButton
							size="small"
							aria-label="close"
							color="inherit"
							onClick={() => this.setState({
								openSnackbar: false,
							})}
						>
							<Icons.Link fontSize="small" />
						</Mui.IconButton>
					}
					autoHideDuration={6000}
					onClose={() => this.setState({
						openSnackbar: false,
					})}
					open={openSnackbar}
				>
					<MuiAlert severity={snackbarSeverity}>
						<Mui.Typography>{snackbarMessage}</Mui.Typography>
					</MuiAlert>
				</Mui.Snackbar>
				{this.renderNavModal()}
			</DashboardLayout>
		);
	}
}

export const SettingsListingResultsPage = withStyles(connect( mapStateToProps, mapDispatchToProps)(Component), styles);
